<template>
<div>
  <button
    type="button"
    id="button"
    class="btn-vue"
    :class="`btn-${size} ${type} ${loading ? 'btn-loading-active' : ''} ${square ? 'square' : ''} ${status} ${accordion ? 'accordion' : ''}`"
    :disabled="loading || type === 'disable' || type === 'ghost disable' ? 'disabled' : false"
    :style="imageURL ? setSizeButtonLeft(size) : ''"
    @click="functionOutput"
  >
    <template v-if="imageURL">
      <div className="btn-image">
        <img :src="imageURL" alt="" />
      </div>
    </template>
    <template v-if="!square">
      <span :style="imageURL && withIcon ? { paddingRight: '32px' } : ''">
        {{label}}
      </span>
    </template>
    <template v-if="withIcon && !square && !loading">
      <i :class="`with-icon ${iconName} ${imageURL ? 'full' : ''}`" />
    </template>
    <template v-if="loading">
      <i
        class="btn-spinner spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      />
    </template>
    <template v-if="iconName && square && !loading">
      <i :class="iconName" />
    </template>
    <template v-if="square && label">
      <div v-b-tooltip.hover :title="square ? label : ''" class="btn-tooltip">{{square ? label : ''}}</div>
    </template>
  </button>
</div>
</template>
<script>

/*
Documentation:

  - label: string variable responsible for button text and tooltip text;
  - type: string variable responsible for button type:
    + primary;
    + secondary;
    + success;
    + warning;
    + danger;
    + ghost;
    + ghost disable;
    + accordion
    + disable;
  - size: string variable responsible for the size of the button:
    + max;
    + mid;
    + min;
    + max full;
    + mid full;
    + min full;
  - square: boolean variable responsible for transforming the square button and adding a tooltip;
  - withIcon: string variable responsible for allowing it to have an icon on the button;
  - iconName: string variable responsible for changing the button's icon;
  - loading: boolean variable responsible for adding a spinner and disabling the button;
*/

export default {
  methods: {
    functionOutput () {
      if (this.data) {
        this.$emit('functionOutput', this.data)
      }
    }
  },
  props: {
    data: { type: Object },
    label: { type: String },
    type: { type: String },
    size: { type: String },
    status: { type: String },
    square: { type: Boolean },
    withIcon: { type: Boolean },
    iconName: { type: String },
    accordion: { type: String },
    loading: { type: Boolean },
    imageURL: { type: String },
    functionInput: { type: Function }
  }
}
</script>
<style lang="scss" scoped>
button {
  &.btn-vue {
    width: fit-content;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 16px;
    border: none;
    font-size: 13px;
    font-weight: 400;
    color: #ffffff;
    line-height: 21px;
    background-color: transparent;
    outline: none;
    &.btn-max {
      height: 38px;
      border-radius: 5px;
      i {
        font-size: 16px;
      }
      &.full {
        width: 100%;
      }
      &.accordion {
        font-size: 14px;
        i {
          width: 38px;
          height: 38px;
        }
      }
      &.square {
        width: 38px;
        padding: 0;
        .btn-spinner {
          margin-left: 0;
        }
        .btn-tooltip {
          bottom: 38px + 6px;
        }
        &:hover {
          .btn-tooltip {
            display: block;
          }
        }
      }
      .btn-image {
        width: 38px - 8;
        height: 38px - 8;
        position: absolute;
        left: 0;
        img {
          height: 38px - 4;
        }
      }
    }
    &.btn-mid {
      height: 30px;
      border-radius: 3px;
      i {
        font-size: 14px;
      }
      &.full {
        width: 100%;
      }
      &.accordion {
        font-size: 12px;
        i {
          width: 30px;
          height: 30px;
        }
      }
      &.square {
        width: 30px;
        padding: 0;
        .btn-spinner {
          margin-left: 0;
        }
        .btn-tooltip {
          bottom: 30px + 6px;
        }
        &:hover {
          .btn-tooltip {
            display: block;
          }
        }
      }
      .btn-image {
        width: 30px - 8;
        height: 30px - 8;
        position: absolute;
        left: 0;
        img {
          height: 30px - 4;
        }
      }
    }
    &.btn-min {
      height: 24px;
      border-radius: 2px;
      i {
        font-size: 12px;
      }
      &.full {
        width: 100%;
      }
      &.accordion {
        font-size: 10px;
        i {
          width: 24px;
          height: 24px;
          border-radius: 5px;
        }
      }
      &.square {
        width: 24px;
        padding: 0;
        .btn-spinner {
          margin-left: 0;
        }
        .btn-tooltip {
          bottom: 24px + 6px;
        }
        &:hover {
          .btn-tooltip {
            display: block;
          }
        }
      }
      .btn-image {
        width: 24px - 8;
        height: 24px - 8;
        position: absolute;
        left: 0;
        position: absolute;
        right: 16px;
        img {
          height: 24px - 4;
        }
      }
    }
    &.primary {
      background-color: #1c56e0;
      &.btn-loading-active {
        background-color: #4285f4;
        cursor: not-allowed;
      }
      &:hover {
        background-color: #4285f4;
      }
    }
    &.secondary {
      background-color: #4285f4;
      &.btn-loading-active {
        background-color: lighten(#4285f4, 15%);
        cursor: not-allowed;
      }
      &:hover {
        background-color: lighten(#4285f4, 15%);
      }
    }
    &.success {
      background-color: #41bd64;
      &.btn-loading-active {
        background-color: lighten(#41bd64, 15%);
        cursor: not-allowed;
      }
      &:hover {
        background-color: lighten(#41bd64, 15%);
      }
    }
    &.warning {
      background-color: #f09b1b;
      &.btn-loading-active {
        background-color: lighten(#f09b1b, 15%);
        cursor: not-allowed;
      }
      &:hover {
        background-color: lighten(#f09b1b, 15%);
      }
    }
    &.danger {
      background-color: #ba2129;
      &.btn-loading-active {
        background-color: lighten(#ba2129, 15%);
        cursor: not-allowed;
      }
      &:hover {
        background-color: lighten(#ba2129, 15%);
      }
    }
    &.ghost {
      color: #1c56e0;
      background-color: transparent;
      &.btn-loading-active {
        color: #4285f4;
        background-color: transparent;
        cursor: not-allowed;
        &:hover {
          background-color: transparent;
        }
      }
    }
    &.accordion {
      padding: 0;
      font-weight: 500;
      line-height: 18px;
      color: lighten(#4285f4, 15%);
      background-color: transparent;
      i {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 4px;
        color: #1c56e0;
        border-radius: 5px;
        background-color: lighten(#4285f4, 35%);
        &::before {
          margin-left: -16px;
        }
      }
    }
    &.disable {
      color: #aeaeae;
      background-color: #e5e5e5;
      cursor: not-allowed;
    }
    .with-icon {
      padding-left: 16px;
      &.full {
        position: absolute;
        right: 16px;
      }
    }
    .btn-image {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 4px;
      padding: 0;
      border-radius: 2.5px;
      overflow: hidden;
      background-color: #ffffff;
      img {
        object-fit: cover;
      }
    }
    .btn-spinner {
      margin-left: 16px;
    }
    .btn-tooltip {
      position: absolute;
      display: none;
      width: max-content;
      padding: 2px 5px;
      border-radius: 2.5px;
      background-color: #000000;
      color: #ffffff;
      font-size: 10px;
      font-weight: 600;
      line-height: 18px;
      &::after {
        content: "";
        position: absolute;
        top: 22px;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: #000000 transparent transparent transparent;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/order/components/Button.vue